import dayjs from "dayjs";

// Pure mapping from stored appointment documents to react-big-calendar events.
//
// Stored shapes (see models/appointmentModel.js):
//  - single appointment: `start` / `end` are full instants (date + time), written by
//    AddAppointment's RangePicker (showTime) or CalendarDrawer as ISO strings.
//  - proposed schedule rows (`appointmentSchedules`): `{ date, startTime, endTime }`, each an
//    ISO instant from separate Date/Time pickers, so the day comes from `date` and the clock
//    time from `startTime` / `endTime` — combined in local time, the same way they were picked.
//    Newer rows may also carry pre-combined `start` / `end`, which win when present.

const toDay = value => {
  if (value === null || value === undefined || value === "") return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

// Day of `date` with the clock time of `time` (local time on both sides).
export const combineDateAndTime = (date, time) => {
  const day = toDay(date);
  const clock = toDay(time);
  if (!day) return null;
  if (!clock) return null;
  return day
    .hour(clock.hour())
    .minute(clock.minute())
    .second(clock.second())
    .millisecond(0)
    .toDate();
};

const isMidnight = d => d.hour() === 0 && d.minute() === 0 && d.second() === 0;

// Timed event from two instants; falls back to all-day only when there is genuinely no time.
const buildRange = (startValue, endValue) => {
  const start = toDay(startValue);
  if (!start) return null;
  const end = toDay(endValue);
  if (!end) {
    // No end stored: a date-only start becomes an all-day event, otherwise a zero-length one.
    return isMidnight(start)
      ? { start: start.toDate(), end: start.add(1, "day").toDate(), allDay: true }
      : { start: start.toDate(), end: start.toDate(), allDay: false };
  }
  if (isMidnight(start) && isMidnight(end) && !end.isAfter(start)) {
    return { start: start.toDate(), end: start.add(1, "day").toDate(), allDay: true };
  }
  if (end.isBefore(start)) {
    return { start: start.toDate(), end: start.toDate(), allDay: false };
  }
  return { start: start.toDate(), end: end.toDate(), allDay: false };
};

// One schedule row -> { start, end, allDay } or null.
export const scheduleRowRange = row => {
  if (!row) return null;
  if (toDay(row.start)) return buildRange(row.start, row.end);
  const day = toDay(row.date);
  if (!day) return null;
  const start = combineDateAndTime(row.date, row.startTime);
  if (!start) {
    // Date without any time: all-day.
    const s = day.startOf("day");
    return { start: s.toDate(), end: s.add(1, "day").toDate(), allDay: true };
  }
  let end = combineDateAndTime(row.date, row.endTime);
  // An end time before the start time means the slot runs past midnight.
  if (end && end < start) end = dayjs(end).add(1, "day").toDate();
  return { start, end: end || start, allDay: false };
};

// Appointment documents -> calendar events. Each event keeps the original document fields
// (so the edit dialog gets `_id`, `title`, `desc`, `employees`, ...) with `start` / `end` as
// real Date objects. Rows without a usable date are skipped rather than crashing the calendar.
export const appointmentsToEvents = (appointments = []) => {
  const events = [];
  (appointments || []).forEach(appointment => {
    if (!appointment) return;
    const main = buildRange(appointment.start, appointment.end);
    if (main) {
      // storedStart/storedEnd keep the untouched stored values so the edit dialog round-trips
      // them exactly (an all-day event's display end is widened to the next midnight).
      events.push({
        ...appointment,
        ...main,
        storedStart: appointment.start,
        storedEnd: appointment.end
      });
      return;
    }
    const rows = Array.isArray(appointment.appointmentSchedules)
      ? appointment.appointmentSchedules
      : [];
    rows.forEach((row, scheduleIndex) => {
      const range = scheduleRowRange(row);
      if (range) events.push({ ...appointment, ...range, scheduleIndex });
    });
  });
  return events;
};
